import React, { ReactNode, useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip, Form } from 'react-bootstrap';
import classnames from 'classnames';
import Modal, { ModalProps } from 'react-bootstrap/Modal';

import styles from './formModalMultiStep.module.scss';
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  UseFormReturn,
} from 'react-hook-form';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import { useStepsLogic } from './hooks';

interface StepAction {
  name: string;
  handler: 'close' | 'submit';
  show: boolean;
  tooltip: string;
}

export interface StepBodyProps {
  isStepActive: boolean;
  goToStep: (id: string | number) => void;
}

export interface Step {
  name: string;
  header?: ReactNode;
  body: (stepBodyProps: StepBodyProps) => JSX.Element & {
    Header?: JSX.Element;
  };
  cancel?: Partial<StepAction>;
  previous?: Partial<StepAction>;
  next?: Partial<StepAction>;
}

interface FormModalProps<FormFields extends FieldValues> extends ModalProps {
  show?: boolean;
  onClose: () => void;
  onSubmit: SubmitHandler<FormFields>;
  title: string;
  cancelBtnText?: string;
  className?: string;
  isLoading?: boolean;
  formContext: UseFormReturn<FormFields>;
  steps: Step[];
  errors?: Step[];
  isSuccess: boolean;

  /**
   * Key used to reset stepper on change without triggering again
   * the modal opening animation.
   */
  stepsRenderKey?: string;
}

// eslint-disable-next-line complexity
const FormModalMultiStep = <FormFields extends FieldValues>({
  show = true,
  onClose,
  onSubmit,
  title,
  cancelBtnText = 'Cancel',
  className,
  formContext,
  isLoading,
  steps,
  errors = [],
  isSuccess,
  stepsRenderKey,
  ...modalProps
}: FormModalProps<FormFields>) => {
  const {
    reset: resetForm,
    formState: { isValid: isFormValid },
    handleSubmit,
    control: { _fields },
    trigger,
  } = formContext;

  const {
    step,
    nextStep,
    previousStep,
    goToStep,
    stepPointer,
    hasNextStep,
    resetSteps,
  } = useStepsLogic({
    steps,
    stepErrors: errors,
    onStepChange: () => trigger(),
  });

  const [localStepsRenderKey, setLocalStepsRenderKey] = useState<
    string | undefined
  >(stepsRenderKey);

  useEffect(() => {
    if (localStepsRenderKey !== stepsRenderKey) {
      resetSteps();
      setLocalStepsRenderKey(stepsRenderKey);
    }
  }, [stepsRenderKey]);

  const handleOnEntered = () => {
    // focus first input
    Object.values(_fields)[0]?._f?.ref?.focus?.();
  };

  const handleOnClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (isSuccess && hasNextStep) {
      nextStep();
    }
  }, [isSuccess]);

  const submitDisabled = isLoading || !isFormValid;

  return (
    <Modal
      {...modalProps}
      show={show}
      className={classnames(className, styles.modal)}
      centered={true}
      animation={true}
      onHide={handleOnClose}
      onExited={handleOnClose}
      onEntered={handleOnEntered}
    >
      <Form
        onSubmit={handleSubmit((...props) => {
          onSubmit(...props);
        })}
      >
        <Modal.Header className={classnames('align-items-center')}>
          <Modal.Title>{step?.header || title}</Modal.Title>
        </Modal.Header>

        <Modal.Body className={classnames(styles.formBody)}>
          <FormProvider key={steps.length} {...formContext}>
            {steps.map((step, index) => (
              <div
                className={classnames(styles.formBodySeparation)}
                key={step.name}
                hidden={index !== stepPointer}
              >
                {step.body({
                  isStepActive: index === stepPointer,
                  goToStep,
                })}
              </div>
            ))}
            {stepPointer === Infinity &&
              step?.body({ isStepActive: true, goToStep })}
          </FormProvider>
        </Modal.Body>

        <Modal.Footer>
          <>
            {step?.cancel?.show !== false && (
              <button
                className={classnames('btn btn-primary-outline btn-secondary')}
                onClick={handleOnClose}
                type="button"
              >
                {cancelBtnText}
              </button>
            )}

            <div
              className={classnames(
                'd-flex ml-auto',
                styles.actionButtonsContainer
              )}
            >
              {stepPointer > 0 && step?.previous?.show !== false && (
                <button
                  className="btn btn-light"
                  onClick={previousStep}
                  disabled={isLoading}
                  type="button"
                >
                  PREVIOUS
                </button>
              )}

              <OverlayTrigger
                placement={'top'}
                trigger={step?.next?.tooltip ? undefined : []}
                overlay={
                  <Tooltip
                    id={`form-error-message-tooltip`}
                    className={styles.tooltip}
                  >
                    <p className={styles.tooltipRendered}>
                      {step?.next?.tooltip}
                    </p>
                  </Tooltip>
                }
              >
                <div>
                  {step?.next?.show !== false &&
                    step?.next?.handler === undefined &&
                    stepPointer < Infinity && (
                      <button
                        className="btn btn-primary"
                        disabled={submitDisabled}
                        onClick={nextStep}
                        type="button"
                      >
                        {step?.next?.name || 'NEXT'}
                      </button>
                    )}
                  {step?.next?.show !== false &&
                    step?.next?.handler === 'close' &&
                    stepPointer < Infinity && (
                      <button
                        className="btn btn-primary"
                        disabled={submitDisabled}
                        onClick={onClose}
                        type="button"
                      >
                        {step?.next?.name || 'NEXT'}
                      </button>
                    )}
                  {step?.next?.show !== false &&
                    step?.next?.handler === 'submit' &&
                    stepPointer < Infinity && (
                      <button
                        className="btn btn-primary"
                        disabled={submitDisabled}
                        type="submit"
                      >
                        {isLoading ? (
                          <LoadingIndicator
                            className={classnames(styles.loadingIndicator)}
                          />
                        ) : (
                          step?.next?.name || 'NEXT'
                        )}
                      </button>
                    )}
                </div>
              </OverlayTrigger>
            </div>
          </>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default FormModalMultiStep;
